import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { Field } from '@crave/ui';
import { api } from '../api.ts';
import { Notice, Section, Table, money, when } from '../components/ui.tsx';

interface Row {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  has_account: boolean;
  balance_cents: number;
  completed_orders: number;
  lifetime_cents: number;
  last_order_at: string | null;
}

/** Set by the customer page after a delete, so this page can say what happened. */
export interface CustomersLocationState {
  deleted?: string;
}

export function Customers() {
  const location = useLocation();
  const navigate = useNavigate();
  const deleted = (location.state as CustomersLocationState | null)?.deleted;
  const [done] = useState(deleted ? `Deleted ${deleted}.` : null);
  // Shown once; a reload should not repeat it.
  useEffect(() => {
    if (deleted)
      void navigate(location.pathname, { replace: true, state: null, preventScrollReset: true });
  }, [deleted, navigate, location.pathname]);
  const [q, setQ] = useState('');
  const [term, setTerm] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setTerm(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);
  const list = useQuery({
    queryKey: ['customers', term],
    queryFn: async () =>
      (await api.get<{ items: Row[] }>(`/admin/customers?q=${encodeURIComponent(term)}`)).data
        .items,
  });
  return (
    <Section title="Customers">
      {done && <Notice kind="ok">{done}</Notice>}
      <Field
        label="Search"
        placeholder="Name, email or phone"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      {list.isError && <Notice kind="error">Could not load customers.</Notice>}
      <Table
        head={['Customer', 'Account', 'Orders', 'Spent', 'Crave Credit', 'Last order']}
        rowKeys={(list.data ?? []).map((c) => c.id)}
        rows={(list.data ?? []).map((c) => [
          <Link
            to={`/customers/${c.id}`}
            className="crave-hit"
            style={{ display: 'inline-block', verticalAlign: 'top' }}
          >
            {[c.first_name, c.last_name].filter(Boolean).join(' ') || c.email}
            <div style={{ fontSize: 12, color: 'var(--ink-soft)' }}>{c.email}</div>
          </Link>,
          c.has_account ? 'Account' : 'Guest',
          String(c.completed_orders),
          money(c.lifetime_cents),
          money(c.balance_cents),
          c.last_order_at ? when(c.last_order_at) : 'No orders yet',
        ])}
        empty={list.isLoading ? 'Loading…' : 'No customers yet.'}
      />
    </Section>
  );
}
